// filepath: /src/components/Formularios/QRScanner.js
import React, { useRef, useEffect, useState } from 'react';
import QrScanner from 'qr-scanner';
import './QRScanner.css';

const QRScanner = ({ isOpen, onClose, onScanResult }) => {
  const videoRef = useRef(null);
  const scannerRef = useRef(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const initScanner = async () => {
      if (isOpen && videoRef.current) {
        await startScanner();
      }
    };

    initScanner();

    return () => {
      stopScanner();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const startScanner = async () => {
    try {
      setError('');

      // Verificar si hay cámaras disponibles
      const hasCamera = await QrScanner.hasCamera();
      if (!hasCamera) {
        setError('No se encontró cámara disponible');
        return;
      }

      // Crear el scanner
      scannerRef.current = new QrScanner(
        videoRef.current,
        (result) => {
          handleScanResult(result.data);
        },
        {
          preferredCamera: 'environment', // Usar cámara trasera si está disponible
          highlightScanRegion: true,
          highlightCodeOutline: true,
        }
      );

      await scannerRef.current.start();
    } catch (err) {
      console.error('Error starting QR scanner:', err);
      setError('Error al acceder a la cámara. Verifica los permisos.');
    }
  };

  const stopScanner = () => {
    if (scannerRef.current) {
      scannerRef.current.stop();
      scannerRef.current.destroy();
      scannerRef.current = null;
    }
  };

  const handleScanResult = (data) => {
    try {
      // Intentar parsear el QR como JSON (para datos estructurados)
      let parsedData;
      try {
        parsedData = JSON.parse(data);
      } catch {
        // Si no es JSON, intentar extraer datos de formato CURP o texto
        parsedData = extractDataFromText(data);
      }

      onScanResult(parsedData);
      onClose();
    } catch (err) {
      console.error('Error processing QR data:', err);
      setError('Error al procesar los datos del QR');
    }
  };

  const extractDataFromText = (text) => {
    // Función para extraer datos de texto plano o formatos conocidos
    const data = {};
    
    // Detectar CURP (18 caracteres alfanuméricos específicos)
    const curpMatch = text.match(/[A-Z]{4}[0-9]{6}[HM][A-Z]{5}[0-9]{2}/);
    if (curpMatch) {
      data.curp = curpMatch[0];
      
      // Extraer información del CURP
      const curp = curpMatch[0];
      data.paternalLastName = curp.substring(0, 2);
      data.maternalLastName = curp.substring(2, 3);
      data.name = curp.substring(3, 4);
      
      // Fecha de nacimiento del CURP (YYMMDD)
      const year = parseInt(curp.substring(4, 6));
      const month = curp.substring(6, 8);
      const day = curp.substring(8, 10);
      const fullYear = year < 50 ? 2000 + year : 1900 + year; // Asumiendo que <50 es 20xx
      data.birthDate = `${fullYear}-${month}-${day}`;
      
      // Sexo
      data.gender = curp.substring(10, 11); // H o M
      
      // Estado de nacimiento (simplificado)
      const stateCode = curp.substring(11, 13);
      data.birthState = getStateFromCode(stateCode);
    }

    // Buscar email
    const emailMatch = text.match(/[\\w.-]+@[\\w.-]+\\.\\w+/);
    if (emailMatch) {
      data.email = emailMatch[0];
    }

    // Buscar teléfono
    const phoneMatch = text.match(/(\+52\s?)?(\d{2,3}\s?)?\d{3,4}\s?\d{4}/);
    if (phoneMatch) {
      data.phone = phoneMatch[0].replace(/\s/g, '');
    }

    return data;
  };

  const getStateFromCode = (code) => {
    const stateCodes = {
      'AS': 'Aguascalientes',
      'BC': 'Baja California',
      'BS': 'Baja California Sur',
      'CC': 'Campeche',
      'CS': 'Chiapas',
      'CH': 'Chihuahua',
      'DF': 'Ciudad de México',
      'CL': 'Coahuila',
      'CM': 'Colima',
      'DG': 'Durango',
      'MC': 'Estado de México',
      'GT': 'Guanajuato',
      'GR': 'Guerrero',
      'HG': 'Hidalgo',
      'JC': 'Jalisco',
      'MN': 'Michoacán',
      'MS': 'Morelos',
      'NT': 'Nayarit',
      'NL': 'Nuevo León',
      'OC': 'Oaxaca',
      'PL': 'Puebla',
      'QT': 'Querétaro',
      'QR': 'Quintana Roo',
      'SP': 'San Luis Potosí',
      'SL': 'Sinaloa',
      'SR': 'Sonora',
      'TC': 'Tabasco',
      'TS': 'Tamaulipas',
      'TL': 'Tlaxcala',
      'VZ': 'Veracruz',
      'YN': 'Yucatán',
      'ZS': 'Zacatecas'
    };
    return stateCodes[code] || '';
  };

  const handleClose = () => {
    stopScanner();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="qr-scanner-overlay" onClick={handleClose}>
      <div className="qr-scanner-modal" onClick={(e) => e.stopPropagation()}>
        <div className="qr-scanner-header">
          <h3>Escanear Código QR</h3>
          <button className="qr-close-button" onClick={handleClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <div className="qr-scanner-content">
          {error ? (
            <div className="qr-error">
              <div className="error-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <path d="M15 9l-6 6M9 9l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <p>{error}</p>
              <button className="glass-button secondary" onClick={handleClose}>
                Cerrar
              </button>
            </div>
          ) : (
            <>
              <div className="qr-camera-container">
                <video ref={videoRef} className="qr-video" />
                <div className="qr-scan-frame">
                  <div className="qr-corner top-left"></div>
                  <div className="qr-corner top-right"></div>
                  <div className="qr-corner bottom-left"></div>
                  <div className="qr-corner bottom-right"></div>
                </div>
              </div>
              
              <div className="qr-instructions">
                <p>Coloca el código QR dentro del marco</p>
                <p>Compatible con CURP, credenciales médicas y datos estructurados</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default QRScanner;
